// Pieza 3: la línea de tiempo (diseño 4a, §2.5). Una fila por episodio con fecha, agrupadas por sancionado. Lo que consta va en
// barra sólida; un año o un mes sin día, en tono claro con bordes firmes (nunca se rellena el día); lo que no sabemos va rayado,
// sólo si la base declara el hueco (`vigencia` o `actas_posteriores` que afecta al hallazgo). La pieza no recalcula «antes» ni
// «después»: dibuja las fechas del documento y la marca de cada sanción.
import type { Episodio, Hallazgo, Hallazgos } from '../hallazgos/tipos.ts';
import type { Caja, Dibujo, Elemento } from './dibujo.ts';
import { acomodar, BORDE, cajaTexto, medir, type Peso, SEPARACION } from './medida.ts';
import { fechaLlana, ROTULOS, rotulo } from './rotulos.ts';

const T = {
  papel: { tam: 12, peso: 500 as Peso },
  nombre: { tam: 11, peso: 400 as Peso },
  marca: { tam: 11, peso: 600 as Peso },
  ultima: { tam: 11, peso: 500 as Peso },
  eje: { tam: 11, peso: 400 as Peso },
  sinActas: { tam: 11, peso: 500 as Peso },
};
const GROSOR_BARRA = 8;
const ALTO_FILA_MIN = 26;

/** Un periodo de una fecha del documento: «2015» es todo 2015, «2015-03» todo marzo, «2015-03-02» ese día. En años con fracción. */
export function periodo(f: string): { ini: number; fin: number; parcial: boolean } {
  const [a, m, d] = f.split('-').map(Number) as [number, number | undefined, number | undefined];
  if (m === undefined) return { ini: a, fin: a + 1, parcial: true };
  if (d === undefined) return { ini: a + (m - 1) / 12, fin: a + m / 12, parcial: true };
  const dias = (Date.UTC(a + 1, 0, 1) - Date.UTC(a, 0, 1)) / 864e5;
  const t = a + (Date.UTC(a, m - 1, d) - Date.UTC(a, 0, 1)) / 864e5 / dias;
  return { ini: t, fin: t, parcial: false };
}

/** Hasta dónde consta un episodio: «terminó» con su fecha; si no, la última acta que lo muestra. */
export function finDeLoQueConsta(e: Episodio): string | null {
  return e.estado === 'termino' ? e.hasta : e.ultima_acta ?? e.hasta;
}

interface Fila {
  h: Hallazgo;
  e: Episodio;
  i: number;
}

/** La línea de tiempo de una ficha; null si ningún episodio tiene fecha. */
export function lineaDeTiempo(doc: Hallazgos, op: { ancho: number }): Dibujo | null {
  const ancho = Math.max(240, Math.round(op.ancho));
  // Filas agrupadas por sancionado (en el orden de su hallazgo de más peso) y, dentro, por peso.
  const orden = new Map<string, number>();
  for (const h of [...doc.hallazgos].sort((a, b) => a.peso - b.peso)) if (!orden.has(h.contraparte.ref)) orden.set(h.contraparte.ref, orden.size);
  const filas: Fila[] = [...doc.hallazgos]
    .sort((a, b) => orden.get(a.contraparte.ref)! - orden.get(b.contraparte.ref)! || a.peso - b.peso)
    .flatMap((h) => h.episodios.map((e, i) => ({ h, e, i })))
    .filter(({ e }) => e.desde || e.hasta || e.ultima_acta);
  if (filas.length === 0) return null;

  // Marcas de sanción de los sancionados de las filas, en rojo; sin fecha no hay marca (lo dice el hueco fecha_sancion).
  const refs = [...new Set(filas.map((f) => f.h.contraparte.ref))];
  const marcas = refs.flatMap((ref) => {
    const s = doc.sancionados[ref];
    return s?.en_lista.desde ? [{ ref, nombre: s.nombre, fecha: s.en_lista.desde }] : [];
  });
  const ultimas = [...new Set(filas.map((f) => f.e.ultima_acta).filter((x): x is string => !!x))].sort();

  // Escala: del primer año con dato al corte de la lista de OFAC (o a la fecha del documento si no hay corte).
  const finEscala = periodo(doc.cortes.lista_ofac ?? doc.generado.slice(0, 10)).fin;
  const fechas = [...filas.flatMap((f) => [f.e.desde, f.e.hasta, f.e.ultima_acta]), ...marcas.map((m) => m.fecha)].filter((x): x is string => !!x);
  let iniEscala = Math.floor(Math.min(...fechas.map((f) => periodo(f).ini)));
  if (finEscala - iniEscala < 3) iniEscala = Math.floor(finEscala - 3);

  // Columna de rótulos de fila y área del dibujo.
  const anchoRotulo = Math.min(260, Math.max(110, Math.round(ancho * 0.3)));
  const x0 = BORDE + anchoRotulo + SEPARACION * 2;
  const x1 = ancho - BORDE - 2;
  const x = (t: number) => x0 + ((Math.min(Math.max(t, iniEscala), finEscala) - iniEscala) / (finEscala - iniEscala)) * (x1 - x0);

  const elementos: Elemento[] = [];
  const textos: Elemento[] = [];
  let y = BORDE;

  // Rótulos de las marcas de sanción, cada uno en su renglón, arriba del área.
  const altoMarca = medir('Ag', T.marca.tam, T.marca.peso).h;
  const textoMarca = (m: (typeof marcas)[number]) => rotulo(ROTULOS.sanciona, { nombre: m.nombre, fecha: fechaLlana(m.fecha) });
  const ubicaciones: { m: (typeof marcas)[number]; xm: number }[] = [];
  for (const [k, m] of marcas.entries()) {
    const xm = x(periodo(m.fecha).ini);
    const completo = textoMarca(m);
    const linea = acomodar(completo, ancho - 2 * BORDE, 1, T.marca.tam, T.marca.peso).lineas[0]!;
    const w = medir(linea, T.marca.tam, T.marca.peso).w;
    // Pegado a la marca por la derecha (como la guía) si cabe; si no, por la izquierda; si tampoco, dentro del lienzo.
    let ancla: 'inicio' | 'fin' = 'fin', tx = xm;
    if (xm - w < BORDE) {
      ancla = 'inicio';
      tx = Math.min(xm, ancho - BORDE - w);
    }
    const base = y + medir(linea, T.marca.tam, T.marca.peso).arriba;
    const id = `marca:${k}`;
    textos.push({
      tipo: 'texto', id: `${id}:texto`, x: ancla === 'inicio' ? tx + 2 : tx - 2, y: base, ancla, tam: T.marca.tam, peso: T.marca.peso, tinta: 'rojo',
      contenido: linea, completo, origen: 'rotulo', caja: cajaTexto(ancla === 'inicio' ? tx + 2 : tx - 2, base, ancla, linea, T.marca.tam, T.marca.peso),
      duenio: id,
    });
    ubicaciones.push({ m, xm });
    y += altoMarca;
  }
  y += SEPARACION;
  const yArriba = y;

  // Filas.
  const altoPapel = medir('Ag', T.papel.tam, T.papel.peso).h;
  const altoNombre = medir('Ag', T.nombre.tam, T.nombre.peso).h;
  const rayables = new Map<string, string>();
  for (const q of doc.huecos) if (q.tipo === 'vigencia' || q.tipo === 'actas_posteriores') for (const h of q.afecta) if (!rayables.has(h)) rayables.set(h, q.id);
  const sinActas: { caja: Caja; id: string }[] = [];
  for (const f of filas) {
    const id = `fila:${f.h.id}:${f.i}`;
    const papel = acomodar(f.e.papel_texto, anchoRotulo, 2, T.papel.tam, T.papel.peso).lineas;
    const otro = f.e.sociedad?.nombre ?? f.h.contraparte.nombre;
    const nombre = acomodar(otro, anchoRotulo, 2, T.nombre.tam, T.nombre.peso).lineas;
    const altoRotulo = papel.length * altoPapel + nombre.length * altoNombre;
    const alto = Math.max(ALTO_FILA_MIN, altoRotulo + SEPARACION);
    let yr = y + (alto - altoRotulo) / 2;
    for (const [li, l] of papel.entries()) {
      const m = medir(l, T.papel.tam, T.papel.peso);
      textos.push({
        tipo: 'texto', id: `${id}:papel${li}`, x: BORDE + 2, y: yr + m.arriba, ancla: 'inicio', tam: T.papel.tam, peso: T.papel.peso, tinta: 'tinta',
        contenido: l, completo: f.e.papel_texto, origen: 'documento', caja: { x: BORDE, y: yr, w: m.w, h: m.h },
      });
      yr += altoPapel;
    }
    for (const [li, l] of nombre.entries()) {
      const m = medir(l, T.nombre.tam, T.nombre.peso);
      textos.push({
        tipo: 'texto', id: `${id}:nombre${li}`, x: BORDE + 2, y: yr + m.arriba, ancla: 'inicio', tam: T.nombre.tam, peso: T.nombre.peso, tinta: 'tenue',
        contenido: l, completo: otro, origen: 'documento', caja: { x: BORDE, y: yr, w: m.w, h: m.h },
      });
      yr += altoNombre;
    }
    // Barras: lo que consta, sólido; los extremos parciales, claros con borde.
    const yb = y + alto / 2 - GROSOR_BARRA / 2;
    const ini = f.e.desde ? periodo(f.e.desde) : null;
    const finF = finDeLoQueConsta(f.e);
    const fin = finF ? periodo(finF) : null;
    const desdeSolido = ini ? (ini.parcial ? ini.fin : ini.ini) : null;
    const hastaSolido = fin ? (fin.parcial ? fin.ini : fin.fin) : null;
    if (ini?.parcial) {
      elementos.push({ tipo: 'rect', id: `${id}:desde`, caja: { x: x(ini.ini), y: yb, w: Math.max(2, x(ini.fin) - x(ini.ini)), h: GROSOR_BARRA }, radio: 0, relleno: 'clienteFondo', borde: 'cliente' });
    }
    if (desdeSolido !== null && hastaSolido !== null && hastaSolido > desdeSolido) {
      elementos.push({ tipo: 'rect', id: `${id}:consta`, caja: { x: x(desdeSolido), y: yb, w: x(hastaSolido) - x(desdeSolido), h: GROSOR_BARRA }, radio: 2, relleno: 'cliente', borde: 'cliente' });
    }
    if (fin?.parcial && finF !== f.e.desde) {
      elementos.push({ tipo: 'rect', id: `${id}:hasta`, caja: { x: x(fin.ini), y: yb, w: Math.max(2, x(fin.fin) - x(fin.ini)), h: GROSOR_BARRA }, radio: 0, relleno: 'clienteFondo', borde: 'cliente' });
    }
    // Una sola fecha (o lo que consta mide cero, o no hay fin): un punto en la fecha completa que haya. Toda fila con fecha se ve.
    const dibujoFila = elementos.some((e) => e.id.startsWith(`${id}:`) && (e.tipo === 'rect' || e.tipo === 'circulo'));
    const punto = !ini?.parcial && ini ? ini.ini : fin && !fin.parcial ? fin.ini : null;
    if (!dibujoFila && punto !== null) {
      elementos.push({ tipo: 'circulo', id: `${id}:punto`, cx: x(punto), cy: y + alto / 2, r: GROSOR_BARRA / 2, relleno: 'cliente', borde: 'cliente' });
    }
    // Lo que no sabemos: rayado de la última acta al fin de la escala, sólo con su hueco.
    const hueco = rayables.get(f.h.id);
    if (hueco && f.e.estado !== 'termino' && f.e.ultima_acta) {
      const xr = x(periodo(f.e.ultima_acta).fin);
      if (x1 - xr > 1) {
        const caja = { x: xr, y: y + 2, w: x1 - xr, h: alto - 4 };
        elementos.push({ tipo: 'rayado', id: `${id}:rayado`, caja, hueco });
        sinActas.push({ caja, id: `${id}:rayado` });
      }
    }
    y += alto;
  }
  const yAbajo = y;

  // Líneas de las marcas: de arriba del área al eje. Sanción en rojo; última acta en gris discontinuo.
  for (const [k, u] of ubicaciones.entries()) {
    elementos.push({ tipo: 'linea', id: `marca:${k}`, puntos: [[u.xm, yArriba], [u.xm, yAbajo + 4]], borde: 'rojo', grosor: 2, discontinua: false, flecha: 'ninguna' });
  }
  const xsUltimas = ultimas.map((u) => x(periodo(u).fin));
  for (const [k, xu] of xsUltimas.entries()) {
    elementos.push({ tipo: 'linea', id: `ultima:${k}`, puntos: [[xu, yArriba], [xu, yAbajo + 4]], borde: 'gris', grosor: 1, discontinua: true, flecha: 'ninguna' });
  }
  // «sin actas: no sabemos» dentro del rayado, donde no lo cruce ninguna marca; si no cabe, sólo el rayado (la leyenda lo explica).
  const verticales = [...ubicaciones.map((u) => u.xm), ...xsUltimas];
  for (const r of sinActas) {
    const linea = ROTULOS.sinActas;
    const m = medir(linea, T.sinActas.tam, T.sinActas.peso);
    const cortes = [r.caja.x, ...verticales.filter((v) => v > r.caja.x && v < r.caja.x + r.caja.w).sort((a, b) => a - b), r.caja.x + r.caja.w];
    let mejor: [number, number] | null = null;
    for (let i = 0; i + 1 < cortes.length; i++) {
      const a = cortes[i]! + 3 + GROSOR_BARRA, b = cortes[i + 1]! - 3 - GROSOR_BARRA;
      if (b - a >= m.w + 2 && (!mejor || b - a > mejor[1] - mejor[0])) mejor = [a, b];
    }
    if (!mejor || m.h + 2 > r.caja.h) continue;
    const cx = (mejor[0] + mejor[1]) / 2;
    const base = r.caja.y + (r.caja.h - m.h) / 2 + m.arriba;
    elementos.push({ tipo: 'rect', id: `${r.id}/fondo`, caja: { x: cx - m.w / 2 - 1, y: base - m.arriba - 1, w: m.w + 2, h: m.h + 2 }, radio: m.h / 2 + 1, relleno: 'blanco', borde: 'blanco' });
    textos.push({
      tipo: 'texto', id: `${r.id}/fondo:texto`, x: cx, y: base, ancla: 'medio', tam: T.sinActas.tam, peso: T.sinActas.peso, tinta: 'tenue', contenido: linea,
      completo: linea, origen: 'rotulo', caja: { x: cx - m.w / 2, y: base - m.arriba, w: m.w, h: m.h }, duenio: `${r.id}/fondo`,
    });
  }

  // Eje con los años; se salta años para que los rótulos no se toquen.
  const yEje = yAbajo + 4;
  elementos.push({ tipo: 'linea', id: 'eje', puntos: [[x0, yEje], [x1, yEje]], borde: 'tenue', grosor: 1, discontinua: false, flecha: 'ninguna' });
  const anchoAnio = medir('0000', T.eje.tam, T.eje.peso).w + SEPARACION;
  const porAnio = (x1 - x0) / (finEscala - iniEscala);
  const paso = [1, 2, 5, 10, 20].find((p) => p * porAnio >= anchoAnio) ?? 50;
  const altoEje = medir('0000', T.eje.tam, T.eje.peso);
  const primero = Math.ceil(iniEscala / paso) * paso;
  let ocupadoHasta = -Infinity;
  for (let a = primero; a <= finEscala; a += paso) {
    const xa = x(a);
    const texto = rotulo(ROTULOS.anio, { anio: a });
    const w = medir(texto, T.eje.tam, T.eje.peso).w;
    if (xa - w / 2 < BORDE || xa + w / 2 > ancho - BORDE || xa - w / 2 < ocupadoHasta) continue;
    elementos.push({ tipo: 'linea', id: `tic:${a}`, puntos: [[xa, yEje], [xa, yEje + 4]], borde: 'tenue', grosor: 1, discontinua: false, flecha: 'ninguna' });
    const base = yEje + 6 + altoEje.arriba;
    textos.push({
      tipo: 'texto', id: `tic:${a}:texto`, x: xa, y: base, ancla: 'medio', tam: T.eje.tam, peso: T.eje.peso, tinta: 'tenue', contenido: texto, completo: texto,
      origen: 'rotulo', caja: cajaTexto(xa, base, 'medio', texto, T.eje.tam, T.eje.peso), duenio: `tic:${a}`,
    });
    ocupadoHasta = xa + w / 2 + SEPARACION;
  }
  y = yEje + 6 + altoEje.h + SEPARACION;

  // «Última acta ([año])», cada una en su renglón, debajo del eje.
  for (const [k, u] of ultimas.entries()) {
    const xu = xsUltimas[k]!;
    const texto = rotulo(ROTULOS.ultimaActa, { anio: u.slice(0, 4) });
    const m = medir(texto, T.ultima.tam, T.ultima.peso);
    let ancla: 'inicio' | 'fin' = 'fin', tx = xu - 2;
    if (xu - m.w < BORDE) {
      ancla = 'inicio';
      tx = Math.min(xu, ancho - BORDE - m.w) + 2;
    }
    const base = y + m.arriba;
    textos.push({
      tipo: 'texto', id: `ultima:${k}:texto`, x: tx, y: base, ancla, tam: T.ultima.tam, peso: T.ultima.peso, tinta: 'gris', contenido: texto, completo: texto,
      origen: 'rotulo', caja: cajaTexto(tx, base, ancla, texto, T.ultima.tam, T.ultima.peso), duenio: `ultima:${k}`,
    });
    y += m.h;
  }

  const descripcion = filas.map((f) => f.h.relacion.frase).filter((v, i, a) => a.indexOf(v) === i).join(' ');
  return { ancho, alto: Math.ceil(y + BORDE), descripcion, elementos: [...elementos, ...textos], fuentes: [], omitidos: 0 };
}
